import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Download,
  AlertTriangle,
  RefreshCw,
  FileText,
  Calendar,
  Hash,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { BrandedLoading, BrandedSpinner } from '@/components/common/BrandedLoading';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { useToast } from '@/hooks/useToast';
import { billingApi } from '@/services/billingApi';
import type { Invoice, InvoiceStatus } from '@/types/billing';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_BADGE: Record<
  InvoiceStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }
> = {
  PAID: { label: 'Paid', variant: 'default', className: 'bg-emerald-600 hover:bg-emerald-600' },
  PENDING: { label: 'Pending', variant: 'outline', className: 'border-amber-500 text-amber-600' },
  SENT: { label: 'Sent', variant: 'outline' },
  OVERDUE: { label: 'Overdue', variant: 'destructive' },
  CANCELLED: { label: 'Cancelled', variant: 'secondary' },
  REFUNDED: { label: 'Refunded', variant: 'secondary', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  PARTIALLY_PAID: { label: 'Partial', variant: 'outline', className: 'border-amber-500 text-amber-600' },
  PAYMENT_FAILED: { label: 'Failed', variant: 'destructive' },
  DRAFT: { label: 'Draft', variant: 'secondary' },
};

const formatCurrencySafe = (amount: unknown, currency = 'USD'): string => {
  const num = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(num);
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InvoiceDetailPage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchInvoice = useCallback(async () => {
    if (!invoiceId) return;
    try {
      setIsLoading(true);
      setError(null);
      const data = await billingApi.getInvoice(invoiceId);
      setInvoice(data);
    } catch (err: unknown) {
      const message = (err as Error)?.message ?? 'Failed to load invoice';
      if (message.includes('Authentication required')) {
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        return;
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [invoiceId, navigate, location.pathname]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    try {
      setIsDownloading(true);
      const blob = await billingApi.getInvoicePdf(invoice.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = (err as Error)?.message ?? '';
      if (message.includes('Authentication required')) {
        navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        return;
      }
      toast({
        title: 'Download failed',
        description: message || 'Could not download the invoice PDF.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // ---- Loading ----
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
          <BrandedLoading message="Loading invoice…" iconSize="md" />
        </main>
        <Footer />
      </div>
    );
  }

  // ---- Error ----
  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
          <div className="text-center space-y-4 max-w-md">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <p className="text-destructive">{error ?? 'Invoice not found'}</p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => navigate('/billing/history')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to History
              </Button>
              <Button onClick={fetchInvoice}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const statusCfg = STATUS_BADGE[invoice.status] ?? STATUS_BADGE.DRAFT;
  const lineItems = invoice.lineItems ?? [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <Header />
      <main className="py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container max-w-3xl mx-auto px-4 sm:px-6 space-y-6">
          {/* Back + heading */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/billing/history')}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Invoices
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <BrandedSpinner className="mr-1" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Download PDF
            </Button>
          </motion.div>

          {/* Invoice header */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                      <CardTitle className="text-xl">Invoice</CardTitle>
                    </div>
                    <p className="text-lg font-semibold flex items-center gap-2">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                      {invoice.invoiceNumber}
                    </p>
                  </div>
                  <Badge variant={statusCfg.variant} className={`${statusCfg.className ?? ''} text-sm px-3 py-1`}>
                    {statusCfg.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Issue Date</p>
                    <p className="font-medium flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {formatDate(invoice.issueDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-medium flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {formatDate(invoice.dueDate)}
                    </p>
                  </div>
                  {invoice.paidDate && (
                    <div>
                      <p className="text-muted-foreground">Paid Date</p>
                      <p className="font-medium">{formatDate(invoice.paidDate)}</p>
                    </div>
                  )}
                  {invoice.billingPeriodStart && invoice.billingPeriodEnd && (
                    <div>
                      <p className="text-muted-foreground">Billing Period</p>
                      <p className="font-medium text-xs">
                        {formatDate(invoice.billingPeriodStart)} &ndash;{' '}
                        {formatDate(invoice.billingPeriodEnd)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Line items */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Line Items</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {lineItems.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground text-sm">
                    No line items available for this invoice.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Unit Price</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lineItems.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrencySafe(item.unitPrice, invoice.currency)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrencySafe(item.amount, invoice.currency)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Price breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-w-xs ml-auto">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatCurrencySafe(invoice.subtotal, invoice.currency)}</span>
                  </div>
                  {invoice.discountAmount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Discount</span>
                      <span className="text-emerald-600">
                        -{formatCurrencySafe(invoice.discountAmount, invoice.currency)}
                      </span>
                    </div>
                  )}
                  {invoice.taxAmount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Tax</span>
                      <span>{formatCurrencySafe(invoice.taxAmount, invoice.currency)}</span>
                    </div>
                  )}
                  <div className="border-t pt-3 flex items-center justify-between">
                    <span className="font-semibold">Total</span>
                    <span className="text-xl font-bold">
                      {formatCurrencySafe(invoice.totalAmount, invoice.currency)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Footer info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xs text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 pb-4"
          >
            <span>Invoice ID: {invoice.id}</span>
            {invoice.type && <span>Type: {invoice.type}</span>}
            {invoice.paymentIntentId && <span>Payment Intent: {invoice.paymentIntentId}</span>}
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default InvoiceDetailPage;
